import { forwardRef, Fragment } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { cn } from 'lib/utils';
import { Icon } from 'components/ui/icon';
import { NavItemIcon } from './nav-item-icon';
import { NavItemLabel } from './nav-item-label';
import { NAV_ITEM_BASE_CLASSES } from './nav-item-constants';
import {
  generateNavPath,
  isExternalUrl,
  getActiveClasses,
  getNestedClasses,
  getCollapsedClasses,
} from './utils';
import type { NavItemProps } from './types';

const NavItem = forwardRef<HTMLAnchorElement, NavItemProps>(
  (
    { item, onNavigate, isCollapsed = false, isNested = false, className },
    ref
  ) => {
    const { orgId } = useParams();
    const path = generateNavPath(item, orgId || '');
    const isExternal = item.external || isExternalUrl(item.path);

    const content = (
      <Fragment>
        <NavItemIcon icon={item.icon} />
        <NavItemLabel isCollapsed={isCollapsed}>{item.name}</NavItemLabel>
      </Fragment>
    );

    const collapsedTitle = isCollapsed ? item.name : undefined;

    const getCommonClasses = (isActive = false) =>
      cn(
        NAV_ITEM_BASE_CLASSES,
        getActiveClasses(isActive),
        getNestedClasses(isNested, isCollapsed, isActive),
        getCollapsedClasses(isCollapsed, isNested),
        className
      );

    if (isExternal) {
      return (
        <a
          ref={ref}
          href={path}
          target="_blank"
          rel="noopener noreferrer"
          className={getCommonClasses()}
          onClick={onNavigate}
          title={collapsedTitle}
        >
          {content}
          {!isCollapsed && (
            <Icon
              name="external-link"
              size={14}
              className="ml-auto shrink-0 opacity-60"
              aria-hidden="true"
            />
          )}
        </a>
      );
    }

    return (
      <NavLink
        ref={ref}
        to={path}
        end
        className={({ isActive }) => getCommonClasses(isActive)}
        onClick={onNavigate}
        title={collapsedTitle}
      >
        {content}
      </NavLink>
    );
  }
);

NavItem.displayName = 'NavItem';

export { NavItem };
